/**
 * audio-playback.ts — AudioContext-backed PCM chunk playback queue.
 *
 * Implements Pattern 4 from 02-RESEARCH.md: gapless playback via chained
 * AudioBufferSourceNode.onended callbacks. Each chunk is decoded from base64
 * Int16 PCM to Float32 and queued. playNext() chains each source node's
 * onended to the next, giving gap-free audio output.
 *
 * Usage:
 *   const queue = new AudioPlaybackQueue({ sampleRate: 16000, onEnded: () => {} });
 *   await queue.resumeAudioContext(); // MUST be called from a user-gesture handler
 *   queue.enqueueBase64(base64PcmString); // call for each audio_chunk frame
 *   queue.flush();                         // call when audio_done received
 *
 * iOS Safari note (Pitfall C):
 *   AudioContext cannot start without a prior user gesture on iOS Safari.
 *   ALWAYS call resumeAudioContext() from an onClick/onTouchStart handler
 *   before the first enqueueBase64 call. Without this, iOS Safari silently
 *   suspends the AudioContext and no audio plays.
 *   The consumer (02-04 UI) MUST wire this to a user-gesture handler.
 *
 * Endianness: Polly emits PCM with OutputFormat='pcm' in little-endian byte order,
 * which matches the x86/ARM native endianness of all supported browser platforms.
 * The Int16Array view of a Uint8Array uses the platform's native byte order.
 */

export interface AudioPlaybackQueueOptions {
  /** Sample rate in Hz — must match Polly's SampleRate ('16000') */
  sampleRate: 16000;
  /** Called when the audio queue drains naturally after flush() was called */
  onEnded?: () => void;
}

export class AudioPlaybackQueue {
  private audioContext: AudioContext | null = null;
  private readonly sampleRate: number;
  private readonly onEnded: (() => void) | undefined;

  private readonly queue: Float32Array[] = [];
  private isPlaying = false;
  private flushed = false;
  private currentSource: AudioBufferSourceNode | null = null;

  constructor(opts: AudioPlaybackQueueOptions) {
    this.sampleRate = opts.sampleRate;
    this.onEnded = opts.onEnded;
  }

  /**
   * Resume (or create) the AudioContext.
   *
   * MUST be called from inside an onClick/onTouchStart handler on iOS Safari.
   * Safe to call multiple times (idempotent if already running).
   */
  async resumeAudioContext(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Decode a base64 PCM chunk and enqueue it for playback.
   *
   * @param base64Pcm  Base64-encoded Int16 PCM at 16kHz mono (from audio_chunk frame)
   */
  enqueueBase64(base64Pcm: string): void {
    // Create AudioContext lazily on first chunk — desktop Chrome allows this without a prior
    // user gesture. iOS Safari requires resumeAudioContext() from a tap first; audio is queued
    // and plays correctly once the context is later resumed.
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
        if (this.audioContext.state === 'suspended') {
          void this.audioContext.resume();
        }
      } catch {
        return; // Blocked by autoplay policy — drop chunk
      }
    }

    // Decode base64 → Uint8Array
    const binary = atob(base64Pcm);
    const pcmBytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      pcmBytes[i] = binary.charCodeAt(i);
    }

    // Reinterpret as Int16 (little-endian, matches Polly PCM output)
    const pcm16 = new Int16Array(pcmBytes.buffer);

    // Convert Int16 → Float32 [-1, 1] per Pattern 4
    // Division by 32768.0 maps [-32768, 32767] to approximately [-1, 1]
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 32768.0;
    }

    this.queue.push(float32);

    // Start playback if idle
    if (!this.isPlaying) {
      this.playNext();
    }
  }

  /**
   * Signal that no more chunks will arrive (audio_done received from server).
   * When the queue drains naturally, onEnded is called.
   * If the queue is already empty at flush time, onEnded fires immediately.
   */
  flush(): void {
    this.flushed = true;
    // If nothing is playing and queue is empty, fire onEnded immediately
    if (!this.isPlaying && this.queue.length === 0) {
      this.onEnded?.();
    }
  }

  /**
   * Stop all playback immediately and clear the queue.
   * Used when a new turn starts or the component unmounts.
   */
  reset(): void {
    this.flushed = false;
    this.queue.length = 0;
    this.isPlaying = false;

    if (this.currentSource) {
      try {
        this.currentSource.onended = null;
        this.currentSource.stop();
      } catch {
        // ignore — source may already be stopped
      }
      this.currentSource = null;
    }
  }

  /**
   * Close the AudioContext and release audio hardware resources.
   * Call on component unmount.
   */
  async close(): Promise<void> {
    this.reset();
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        await this.audioContext.close();
      } catch {
        // ignore
      }
    }
    this.audioContext = null;
  }

  // ── private ────────────────────────────────────────────────────────────────

  private playNext(): void {
    if (this.queue.length === 0) {
      this.isPlaying = false;
      this.currentSource = null;
      // If flushed and queue is now empty, fire onEnded
      if (this.flushed) {
        this.onEnded?.();
      }
      return;
    }

    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.isPlaying = false;
      this.queue.length = 0;
      if (this.flushed) this.onEnded?.();
      return;
    }
    if (this.audioContext.state === 'suspended') {
      this.isPlaying = true; // block concurrent re-entry while resume is in flight
      this.audioContext.resume().then(() => {
        if (this.queue.length > 0) this.playNext();
        else this.isPlaying = false;
      }).catch(() => {
        this.isPlaying = false;
      });
      return;
    }

    this.isPlaying = true;
    const samples = this.queue.shift()!;

    // Create AudioBuffer (Pattern 4: 1 channel, samples.length frames, 16kHz)
    const buffer = this.audioContext.createBuffer(1, samples.length, this.sampleRate);
    buffer.copyToChannel(samples, 0);

    // Create and schedule the source node
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioContext.destination);

    // Chain next chunk: source.onended fires immediately when this chunk ends
    source.onended = () => {
      this.playNext();
    };

    this.currentSource = source;
    source.start(0);
  }
}
